import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiBlobRequest } from "../../../../../shared/api/client";
import type { Attachment } from "../../../domain/request-detail-types";

type RequestScope = {requestNo: string; sourceScope?: object};
type Selection<T> = {scope: RequestScope; value: T};
type Preview = {selection: Selection<Attachment | null> | null; url: string; error: string};
export function useRequestAttachmentsWorkspace({requestNo, scope: sourceScope}: {requestNo: string; scope?: object}) {
  const mounted = useRef(true), currentRequest = useRef<RequestScope>({requestNo, sourceScope});
  if (currentRequest.current.requestNo !== requestNo || currentRequest.current.sourceScope !== sourceScope) currentRequest.current = {requestNo, sourceScope};
  const requestScope = currentRequest.current;
  const [selection, setSelection] = useState<Selection<Attachment | null>>({scope: requestScope, value: null});
  const currentSelection = useRef(selection);
  currentSelection.current = selection;
  const [menu, setMenu] = useState<Selection<string | null>>({scope: requestScope, value: null});
  const [preview, setPreview] = useState<Preview>({selection: null, url: "", error: ""});
  const previewAttachment = selection.scope === requestScope ? selection.value : null;
  const openOfferDocumentsId = menu.scope === requestScope ? menu.value : null;
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const setPreviewAttachment: Dispatch<SetStateAction<Attachment | null>> = useCallback(value => {
    if (!mounted.current || currentRequest.current !== requestScope) return;
    setSelection(current => ({scope: requestScope, value: typeof value === "function" ? value(current.scope === requestScope ? current.value : null) : value}));
  }, [requestScope]);
  const setOpenOfferDocumentsId: Dispatch<SetStateAction<string | null>> = useCallback(value => {
    if (!mounted.current || currentRequest.current !== requestScope) return;
    setMenu(current => ({scope: requestScope, value: typeof value === "function" ? value(current.scope === requestScope ? current.value : null) : value}));
  }, [requestScope]);
  useEffect(() => {
    if (!previewAttachment) return;
    const closeOnEscape = (event: KeyboardEvent) => {if (event.key === "Escape") setPreviewAttachment(null);};
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [previewAttachment, setPreviewAttachment]);
  useEffect(() => {
    if (!openOfferDocumentsId) return;
    const closeDocumentMenu = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest(".detail-offer-documents")) setOpenOfferDocumentsId(null);
    };
    document.addEventListener("mousedown", closeDocumentMenu);
    return () => document.removeEventListener("mousedown", closeDocumentMenu);
  }, [openOfferDocumentsId, setOpenOfferDocumentsId]);
  useEffect(() => {
    if (!previewAttachment) return;
    let active = true, objectUrl = "";
    const isCurrent = () => active && mounted.current && currentRequest.current === requestScope && currentSelection.current === selection;
    void apiBlobRequest(`/api/attachments/${encodeURIComponent(previewAttachment.id)}?preview=1`).then(blob => {
      if (!isCurrent()) return;
      objectUrl = URL.createObjectURL(blob);
      setPreview({selection, url: objectUrl, error: ""});
    }).catch((error: unknown) => {
      if (isCurrent()) setPreview({selection, url: "", error: error instanceof Error ? error.message : "Belge önizlemesi açılamadı."});
    });
    return () => {active = false; if (objectUrl) URL.revokeObjectURL(objectUrl);};
  }, [previewAttachment, selection, requestScope]);
  const visible = selection.scope === requestScope && preview.selection === selection;
  return {previewAttachment, setPreviewAttachment, openOfferDocumentsId, setOpenOfferDocumentsId, previewObjectUrl: visible ? preview.url : "", previewError: visible ? preview.error : ""};
}
